/* EntityTreeGridRenderer.js */

"use strict";

//const _ = require('lodash');
const GridColumn = require('./column/GridColumnComponent');
const GridRow = require('./row/GridRowComponent');
const ExpandableGridRow = require('./expandable_row/ExpandableGridRowComponent');
const BrowserDetect = require('@gluenetworks/srvc_core_browser-detect');
const _ = require('lodash');

class EntityTreeGridRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("entity-tree-grid-gl");
        this._rootElem.component = this._model;
        this._headerRow = document.createElement("header-row");
        this._gridBody = document.createElement("grid-body");
        this._rootElem.appendChild(this._headerRow);
        this._rootElem.appendChild(this._gridBody);

        this._columnComponents = {};
        this._expandableRowComponents = {};
        this._expandableRowComponentsOrder = [];
        this._expandedRows = [];
        this._rowComponents = {};
        
        this._lastRowSelectionId = undefined;
        this._commandKeyDown = false;
        this._sortColumn = undefined;
        this._sortFunction = undefined;
        this._sort = undefined;

        this._registerListeners();
        this.render();
    }
    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _shutdown(){
        let shutdownPromiseArray = [];
        this._unregisterListeners();
        this._model = undefined;
        this._rootElem.remove();
        this._rootElem = undefined;
        this._headerRow = undefined;
        this._gridBody = undefined;

        for (let colKey in this._columnComponents) {
            this._columnComponents[colKey].removeAllListeners('UserSorted');
            shutdownPromiseArray.push(this._columnComponents[colKey].shutdown());
        }
        for (let rowKey in this._rowComponents) {
            shutdownPromiseArray.push(this._deleteRow(rowKey));
        }
        for (let rowKey in this._expandableRowComponents) {
            shutdownPromiseArray.push(this._deleteExpandableRow(rowKey));
        }
        this._columnComponents = undefined;
        this._expandableRowComponents = undefined;
        this._expandableRowComponentsOrder = undefined;
        this._expandedRows = undefined;
        this._rowComponents = undefined;
        this._lastRowSelectionId = undefined;
        this._commandKeyDown = undefined;
        this._sortColumn = undefined;
        this._sortFunction = undefined;
        this._sort = undefined;

        return Promise.all(shutdownPromiseArray);
    }
    _registerListeners(){
        this._model.addListener("Id",this._onId = this._onId.bind(this));
        this._model.addListener("Entities",this._onEntities = this._onEntities.bind(this));
        this._model.addListener("AddEntity",this._onAddEntity = this._onAddEntity.bind(this));
        //this._model.addListener("UpdateEntity",this._onUpdateEntity = this._onUpdateEntity.bind(this));
        this._model.addListener("DeleteEntity",this._onDeleteEntity = this._onDeleteEntity.bind(this));
        this._model.addListener("Columns",this._onColumns = this._onColumns.bind(this));
        this._model.addListener("ColumnOrder",this._onColumnOrder = this._onColumnOrder.bind(this));
        this._model.addListener("SelectedRows",this._onSelectedRows = this._onSelectedRows.bind(this));
        this._model.addListener("Focus",this._onFocus = this._onFocus.bind(this));
        this._model.addListener("Blur",this._onBlur = this._onBlur.bind(this));
        this._rootElem.addEventListener('focus', this._onFocusEvent = this._onFocusEvent.bind(this));
        this._rootElem.addEventListener('blur', this._onBlurEvent = this._onBlurEvent.bind(this));
        if (BrowserDetect.os === 'Mac') {
            window.addEventListener('keydown', this._onKeyDown = this._onKeyDown.bind(this));
            window.addEventListener('keyup', this._onKeyUp = this._onKeyUp.bind(this));
        }
    }
    _unregisterListeners(){
        this._model.removeListener("Id",this._onId);
        this._model.removeListener("Entities",this._onEntities);
        this._model.removeListener("AddEntity",this._onAddEntity);
        //this._model.removeListener("UpdateEntity",this._onUpdateEntity);
        this._model.removeListener("DeleteEntity",this._onDeleteEntity);
        this._model.removeListener("Columns",this._onColumns);
        this._model.removeListener("ColumnOrder",this._onColumnOrder);
        this._model.removeListener("SelectedRows",this._onSelectedRows);
        this._model.removeListener("Focus",this._onFocus);
        this._model.removeListener("Blur",this._onBlur);
        this._rootElem.removeEventListener('focus', this._onFocusEvent);
        this._rootElem.removeEventListener('blur', this._onBlurEvent);
        if (BrowserDetect.os === 'Mac') {
            window.removeEventListener('keydown', this._onKeyDown);
            window.removeEventListener('keyup', this._onKeyUp);
        }
    }
    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering. If a non
     * Promise is returned the caller will assume the Renderer has completed rendering.
     */
    render(){
        let prom = new Promise((resolve/*,reject*/) => {
            this._headerRow.innerHTML = '';
            this._gridBody.innerHTML = '';
            this._setId(this._model.getId());
            this._setColumns(this._model.getColumns());
            this._setColumnOrder(this._model.getColumnOrder());
            let entities = this._model.getEntities();
            this._setEntities(entities);
            this._setSelectedRows(this._model.getSelectedRows());
            if (!_.isUndefined(this._sortColumn)) {
                this._setGridSorted(this._columnComponents[this._sortColumn]);
            }
            resolve();
        });
        return prom;
    }
    get rootElement(){
        return this.getRootElement();
    }
    getRootElement(){
        return this._rootElem;
    }
    get model(){
        return this.getModel();
    }
    getModel(){
        return this._model;
    }
    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /**
     * Fired when the id has been updated in the model
     * @param newVal {string}
     * @private
     */
    _onId(newVal){
        this._setId(newVal);
    }

    /**
     * Fired when the column configuration has been updated in the model
     * @param newVal {object}
     * @private
     */
    _onColumns(newVal){
        this._setColumns(newVal);
    }

    /**
     * Fired when the column order has been updated in the model
     * @param newVal {array}
     * @private
     */
    _onColumnOrder(newVal){
        this._setColumnOrder(newVal);
    }

    /**
     * Fired when the data has been updated in the model
     * @param newVal {object}
     * @private
     */
    _onEntities(newVal){
        this._setEntities(newVal);
    }

    /**
     * Fired when a new entitiy has been added to the model
     * @param newVal {object}
     * @private
     */
    _onAddEntity(newVal){
        this._addEntity(newVal);
        let entityId = newVal.getId(),
            parentRowId = this._rowComponents[entityId].getParentId(),
            parentRow = this._expandableRowComponents[parentRowId];
        this._buildDisplayFromExpandableRowDown(parentRow);
    }

    /**
     * Fired when an entity has been updated in the model
     * @param newVal {object}
     * @private
     */
    _onUpdateEntity(/*newVal*/){
        // this._updateEntity(newVal);
    }

    /**
     * Fired when an entity has been deleted in the model
     * @param rowKey {string}
     * @private
     */
    _onDeleteEntity(rowKey){
        this._deleteEntity(rowKey);
    }

    /**
     * Fired when the selcted rows have been updated in the model
     * @param newVal {array}
     * @private
     */
    _onSelectedRows(newVal){
        this._setSelectedRows(newVal);
    }

    /**
     * Fired when focus has been called in the model
     * @private
     */
    _onFocus(){
        this._setFocus(true);
    }

    /**
     * Fired when blur has been called in the model
     * @private
     */
    _onBlur(){
        this._setFocus(false);
    }

    /**
     * Pass the focus event up to the model
     * @param event {Event}
     * @private
     */
    _onFocusEvent(/*event*/){
        //this._model._triggerUserFocus(event);
    }

    /**
     * Pass the blur event up to the model
     * @param event {Event}
     * @private
     */
    _onBlurEvent(/*event*/){
        //this._model._triggerUserBlur(event);
    }

    /**
     * Catch the command key
     * @param event {Event}
     * @private
     */
    _onKeyDown(event){
        if (event.keyCode === 91 || event.keyCode === 93) {
            this._commandKeyDown = true;
        } else {
            this._commandKeyDown = false;
        }
    }

    /**
     * Catch the command key
     * @param event {event}
     * @private
     */
    _onKeyUp(event){
        if (event.keyCode === 91 || event.keyCode === 93) {
            this._commandKeyDown = false;
        }
    }

    /**
     * Fired when a column has been sorted by the user
     * @param sorted {string}
     * @param column {component}
     * @private
     */
    _onColumnUserSorted(sorted, column){
        this._setGridSorted(column);
    }

    /**
     * Fired when the selected state of a row has changed
     * @param selected {boolean}
     * @param mimicUserCLick {boolean}
     * @param rowComponent {GridRowComponent}
     * @private
     */
    _onRowSelected(selected, mimicUserClick, rowComponent){
        if (mimicUserClick === true) {
            if (selected === true) {
                this._model.addSelectedRow(rowComponent.getId());
            } else {
                this._model.removeSelectedRow(rowComponent.getId());
            }
        }
    }

    /**
     * Fired when the user has selected a row
     * @param rowData {object}
     * @param event {event}
     * @private
     */
    _onRowUserSelected(rowData, event){
        let selectedRows = this._model.getSelectedRows();
        let currentRowSelectionId = event.currentTarget.component.getId();
        let singleSelect = this._model.getSingleSelect();
        if (singleSelect === true) {
            if (event.ctrlKey === true || this._commandKeyDown === true) { // Control/Command Click
                if (selectedRows[0] === currentRowSelectionId) {
                    selectedRows = [];
                } else {
                    selectedRows = [currentRowSelectionId];
                }
            } else {
                selectedRows = [currentRowSelectionId];
            }
        } else {
            if (event.ctrlKey === true || this._commandKeyDown === true) { // Control/Command Click
                let selectionExists = false;
                selectedRows.forEach((row, index) => {
                    if (currentRowSelectionId === row) {
                        selectionExists = true;
                        selectedRows.splice(index, 1);
                    }
                });
                if (!selectionExists) {
                    selectedRows.push(currentRowSelectionId);
                }
            } else if (event.shiftKey === true) { // Shift Click
                if (currentRowSelectionId === this._lastRowSelectionId) {
                    selectedRows = [currentRowSelectionId];
                } else {
                    selectedRows = this._getRangeSelection(currentRowSelectionId, this._lastRowSelectionId);
                }
            } else { // Regular Click
                selectedRows = [currentRowSelectionId];
            }
        }

        this._lastRowSelectionId = currentRowSelectionId;
        this._model.setSelectedRows(selectedRows);
        this._model._triggerUserSelectedRows(selectedRows);
    }

    /**
     * Fired when the user has double clicked a row
     * @param rowData {object}
     * @param event {event}
     * @private
     */
    _onRowUserDoubleClicked(rowData, event){
        let currentRowSelectionId = event.currentTarget.component.getId();
        this._model._triggerUserRowDoubleClicked(currentRowSelectionId);
    }

    /**
     * Fired when the user has selected an action in the cell
     * @param eventData
     * @private
     */
    _onRowAction(eventData){
        this._model._triggerGridAction(eventData);
    }

    /**
     * Fired when the user has toggled an expandable row
     * @param expanded {boolean}
     * @param rowId {string}
     * @private
     */
    _onExpandableRowUserExpanded(expanded, rowId){
        let expandableRow = this._expandableRowComponents[rowId];
        if (expanded === true) {
            this._expandedRows.push(rowId);
            this._buildChildDisplay(expandableRow);
        } else {
            this._expandedRows.splice(this._expandedRows.indexOf(rowId), 1);
            this._removeRowChildren(expandableRow);
        }
    }

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/

    /**
     * Set the DOM id
     * @param newVal {string}
     * @private
     */
    _setId(newVal){
        this._rootElem.classList.add(newVal);
        this._rootElem.dataset.id = newVal;
    }

    /**
     * Build the column display
     * @param newVal {object}
     * @private
     */
    _setColumns(newVal){
        let columnOrder = this._model.getColumnOrder();
        for (let colKey in this._columnComponents) {
            this._columnComponents[colKey].removeAllListeners('UserSorted');
            this._columnComponents[colKey].shutdown();
        }
        this._columnComponents = {};
        columnOrder.forEach((columnKey) => {
            let column = new GridColumn(newVal[columnKey]);
                column.setId(columnKey);
                column.on('UserSorted', (sorted) => {
                    this._onColumnUserSorted(sorted, column);
                });
            if (column.getSort() !== '') {
                this._sortColumn = columnKey;
            }
            this._columnComponents[columnKey] = column;
            this._headerRow.appendChild(column.renderer.rootElement);
        });
    }

    /**
     * Removes any existing sort from columns other than the passed in column
     * @param column {component}
     * @private
     */
    _resetSortDisplay(column){
        for (let colKey in this._columnComponents) {
            let col = this._columnComponents[colKey];
            if (column !== col) {
                col.setSort('');
            }
        }
    }

    /**
     * Order the column display
     * @param newVal {array}
     * @private
     */
    _setColumnOrder(/*newVal*/){
        // TODO: REORDER THE COLUMNS, IF DISPLAYING
    }

    /**
     * Build the entity rows
     * @param newVal {object}
     * @private
     */
    _setEntities(newVal){
        let entityOrder = this._model.getEntityOrder();
        for (let rowKey in this._rowComponents) {
            this._deleteRow(rowKey);
        }
        for (let rowKey in this._expandableRowComponents) {
            this._deleteExpandableRow(rowKey);
        }
        this._rowComponents = {};
        this._expandableRowComponents = {};
        this._expandableRowComponentsOrder = [];
        entityOrder.forEach((entityId) => {
            this._addEntity(newVal[entityId]);
        });
        this._buildDisplay();
    }

    /**
     * Sort the grid based on the passed in column
     * @param column {object}
     * @private
     */
    _setGridSorted(column){
        this._sortColumn = column.getId();
        this._sortFunction = column.getColumnRendererComponent().SortFunction;
        this._sort = column.getSort();
        this._resetSortDisplay(column, this._sort);
        this._sortRows();
        this._model._triggerUserSorted(this._sortColumn, this._sort);
    }

    _buildDisplay(){
        this._gridBody.innerHTML = '';
        this._expandableRowComponentsOrder.forEach((row) => {
            let expandableRowComponent = this._expandableRowComponents[row],
                nestedLevel = expandableRowComponent.getNestedLevel(),
                rowToInsertAfter;
            if (nestedLevel === 0) {
                let expanded = expandableRowComponent.getExpanded();
                this._insertExpandableRow(expandableRowComponent, rowToInsertAfter);
                if (expanded === true) {
                    rowToInsertAfter = this._buildChildDisplay(expandableRowComponent);
                }
            }
        });
    }

    _buildChildDisplay(parentRowComponent){
        let childIds = parentRowComponent.getChildren(),
            orderedChildren = [],
            lastChild,
            expandableRowChildren = (!_.isUndefined(this._expandableRowComponents[childIds[0]])) ? true : false;

        if (expandableRowChildren === true) {
            this._expandableRowComponentsOrder.forEach((rowId) => {
                if (childIds.indexOf(rowId) !== -1) {
                    orderedChildren.push(rowId);
                }
            });
        } else {
            this._model.getEntityOrder().forEach((rowId) => {
                if (childIds.indexOf(rowId) !== -1) {
                    orderedChildren.push(rowId);
                }
            });
        }

        if (expandableRowChildren === true) {
            orderedChildren.forEach((childId, index) => {
                let childExpandableRowComponent = this._expandableRowComponents[childId],
                    expanded = childExpandableRowComponent.getExpanded();
                if (index === 0) {
                    this._insertExpandableRow(childExpandableRowComponent, parentRowComponent);
                } else {
                    this._insertExpandableRow(childExpandableRowComponent, lastChild);
                }
                lastChild = (expanded === true) ? this._buildChildDisplay(childExpandableRowComponent) : childExpandableRowComponent;
            });
        } else {
            orderedChildren.forEach((childId, index) => {
                let childRowComponent = this._rowComponents[childId];
                if (index === 0) {
                    this._insertRow(childRowComponent, parentRowComponent);
                } else {
                    this._insertRow(childRowComponent, lastChild);
                }
                lastChild = childRowComponent;
            });
        }
        return lastChild;
    }


    _buildDisplayFromExpandableRowDown(expandableRowComponent) {
        let dataId = expandableRowComponent.getId();
        let existsInDom = (this._rootElem.querySelector('[data-id="' + dataId + '"]') !== null) ? true : false;
        let nestedLevel = expandableRowComponent.getNestedLevel();
        let expanded = expandableRowComponent.getExpanded();

        if (existsInDom === true && expanded === true) {
            this._removeRowChildren(expandableRowComponent, true);
            this._buildChildDisplay(expandableRowComponent);
        } else {
            if (nestedLevel === 0) {
                let insertIndex = this._expandableRowComponentsOrder.indexOf(dataId);
                if (insertIndex === 0) {
                    this._gridBody.insertBefore(expandableRowComponent.renderer.rootElement, this._gridBody.firstChild);
                } else {
                    let previousExpandableRowComponentId = this._expandableRowComponentsOrder[insertIndex-1];
                    let previousExpandableRowComponent = this._expandableRowComponents[previousExpandableRowComponentId];
                    this._insertAfterExpandableRow(expandableRowComponent, previousExpandableRowComponent);
                }
                if (expanded === true) {
                    this._buildChildDisplay(expandableRowComponent);
                }
            } else {
                let parentExpandableRowComponentId = expandableRowComponent.getParentId();
                let parentExpandableRowComponent = this._expandableRowComponents[parentExpandableRowComponentId];
                this._buildDisplayFromExpandableRowDown(parentExpandableRowComponent);
            }
        }
    }



    /**
     * Add an entity and build the row and expandable rows as necessary
     * @param newVal {object}
     * @private
     */
    _addEntity(newVal){
        let entity = newVal,
            entityId = entity.getId(),
            entityPath = entity.getProperty('fullDisplayPath'),
            nestedConfig = entityPath.split('/');
        for (let i=0; i < nestedConfig.length - 1; i++) {
            let pathNodeName = nestedConfig[i],
                nestedLevel = i,
                expandableRowId = '',
                expandableRowParentId = '';
            for (let j=0; j <= i; j++) {
                expandableRowId += '/' + nestedConfig[j];
                if (i !== j) {
                    expandableRowParentId += '/' + nestedConfig[j];
                }
            }
            let entityExpanded = this._expandedRows.includes(expandableRowId);
            if (_.isUndefined(this._expandableRowComponents[expandableRowId])) {
                let expandableRow = new ExpandableGridRow({
                    id: expandableRowId,
                    name: pathNodeName,
                    parentId: expandableRowParentId,
                    columnOrder: this._model.getColumnOrder(),
                    columnComponents: this._columnComponents,
                    nestedLevel: nestedLevel,
                    expanded: entityExpanded
                });
                this._expandableRowComponents[expandableRowId] = expandableRow;
                this._expandableRowComponents[expandableRowId].on('RowUserExpanded', this._onExpandableRowUserExpanded = this._onExpandableRowUserExpanded.bind(this));
                if (!_.isUndefined(this._expandableRowComponents[expandableRowParentId])) {
                    this._expandableRowComponents[expandableRowParentId].addChild(expandableRowId);
                }
            } else if (entityExpanded === true) {
                this._expandableRowComponents[expandableRowId].setExpanded(true);
            }
        }
        this._addToExpandableRowComponentsOrder(entityId, entityPath);

        let rowParentId = '';
        for (let i=0; i < nestedConfig.length - 1; i++) {
            rowParentId += '/' + nestedConfig[i];
        }
        let row = new GridRow({
            id: entityId,
            columnOrder: this._model.getColumnOrder(),
            entity: entity,
            columnComponents: this._columnComponents,
            nestedLevel: nestedConfig.length - 1,
            parentId: rowParentId
        });
        this._rowComponents[entityId] = row;
        this._rowComponents[entityId].addListener('RowUserSelected', this._onRowUserSelected = this._onRowUserSelected.bind(this));
        this._rowComponents[entityId].addListener('Selected', (selected, mimicUserClick) => {
            this._onRowSelected.call(this, selected, mimicUserClick, this._rowComponents[entityId]);
        });
        this._rowComponents[entityId].addListener('RowUserDoubleClicked', this._onRowUserDoubleClicked = this._onRowUserDoubleClicked.bind(this));
        this._rowComponents[entityId].addListener('RowAction', this._onRowAction = this._onRowAction.bind(this));

        if (!_.isUndefined(this._expandableRowComponents[rowParentId])) {
            let parentExpandableRow = this._expandableRowComponents[rowParentId];
                parentExpandableRow.addChild(entityId);
        }
    }

    _addToExpandableRowComponentsOrder(entityId, entityPath){
        let entityOrder = this._model.getEntityOrder(),
            entityIndex = entityOrder.indexOf(entityId),
            previousEntityIndex = entityIndex - 1,
            previousEntityId = (previousEntityIndex !== -1) ? entityOrder[previousEntityIndex] : undefined,
            previousEntityParentExpandableRowId = (!_.isUndefined(previousEntityId)) ? this._rowComponents[previousEntityId].getParentId() : undefined,
            nestedConfig = entityPath.split('/');
        for (let i=0; i < nestedConfig.length; i++) {
            let expandableRowId = '',
                insertIndex = 0;
            for (let j=0; j < i; j++) {
                expandableRowId += '/' + nestedConfig[j];
            }
            if (expandableRowId !== '' && this._expandableRowComponentsOrder.indexOf(expandableRowId) === -1) {
                if (!_.isUndefined(previousEntityParentExpandableRowId)) {
                    insertIndex = this._expandableRowComponentsOrder.indexOf(previousEntityParentExpandableRowId) + i;
                } else {
                    insertIndex = i;
                }
                this._expandableRowComponentsOrder.splice(insertIndex, 0, expandableRowId);
            }
        }
    }

    _insertRow(rowComponent, previousRowComponent){
        if (!_.isUndefined(previousRowComponent)) {
            let domNodeToInsertAfter = previousRowComponent.renderer.rootElement;
            domNodeToInsertAfter.parentNode.insertBefore(rowComponent.renderer.rootElement, domNodeToInsertAfter.nextSibling);
        } else {
            this._gridBody.appendChild(rowComponent.renderer.rootElement);
        }
    }

    _insertExpandableRow(expandableRowComponent, previousRowComponent){
        if (!_.isUndefined(previousRowComponent)) {
            let domNodeToInsertAfter = previousRowComponent.renderer.rootElement;
            domNodeToInsertAfter.parentNode.insertBefore(expandableRowComponent.renderer.rootElement, domNodeToInsertAfter.nextSibling);
        } else {
            this._gridBody.appendChild(expandableRowComponent.renderer.rootElement);
        }
    }

    _insertAfterExpandableRow(expandableRowComponent, previousExpandableRowComponent){
        let dataId = expandableRowComponent.getId(),
            existsInDom = (this._rootElem.querySelector('[data-id="' + dataId + '"]') !== null) ? true : false,
            previousRowExpanded = previousExpandableRowComponent.getExpanded(),
            previousRowChildren = previousExpandableRowComponent.getChildren();
        if (existsInDom === true) {
            if (previousRowExpanded === true) {
                let lastChildIndex = previousRowChildren.length - 1,
                    lastChildId = previousRowChildren[lastChildIndex],
                    previousRowComponent = this._rowComponents[lastChildId];
                this._insertExpandableRow(expandableRowComponent, previousRowComponent);
            } else {
                this._insertExpandableRow(expandableRowComponent, previousExpandableRowComponent);
            }
        } else {
            let previousParentExpandableRowComponentId = previousExpandableRowComponent.getParentId(),
                previousParentExpandableRowComponent = this._expandableRowComponents[previousParentExpandableRowComponentId];
            this._insertAfterExpandableRow(expandableRowComponent, previousParentExpandableRowComponent);
        }
    }


    /**
     * Update an entity
     * @param newVal {object}
     * @private
     */
    // _updateEntity(newVal){
    //     let entity = newVal.entity,
    //         entityName = entity.getProperty('name');
    //     this._rowComponents[entityName].setEntity(entity);
    // }

    /**
     * Delete an entity and update the display
     * @param newVal {string} - Key of the entity to delete
     * @private
     */
    _deleteEntity(newVal){
        let entityDeleteId = newVal,
            entityRow = this._rowComponents[entityDeleteId];
        if (!_.isUndefined(entityRow.getParentId())) {
            let parentExpandableRow = this._expandableRowComponents[entityRow.getParentId()],
                newChildren = [],
                oldChildren = parentExpandableRow.getChildren();
            oldChildren.forEach((childId) => {
                if (childId !== entityDeleteId) {
                    newChildren.push(childId);
                }
            });
            parentExpandableRow.setChildren(newChildren);
        }
        this._deleteRow(entityDeleteId);
    }

    /**
     * Delete a row
     * @param rowKey {string}
     * @private
     */
    _deleteRow(rowKey){
        let rowToDelete = this._rowComponents[rowKey];
        if (!_.isUndefined(this._rowComponents[rowKey])) {
            this._rowComponents[rowKey].removeListener('RowUserSelected', this._onRowUserSelected);
            this._rowComponents[rowKey].removeAllListeners('Selected');
            this._rowComponents[rowKey].removeListener('RowUserDoubleClicked', this._onRowUserDoubleClicked);
            this._rowComponents[rowKey].removeListener('RowAction', this._onRowAction);
            delete this._rowComponents[rowKey];
            return rowToDelete.shutdown();
        }
    }

    /**
     * Remove a grid row from the DOM but keep the component intact
     * @param rowComponent {GridRowComponent}
     * @private
     */
    _removeRow(rowComponent, keepState){
        if (keepState !== true) {
            rowComponent.setSelected(false, true);
        }
        rowComponent.renderer.rootElement.remove();
    }

    /**
     * Delete an expandable row
     * @param rowKey
     * @private
     */
    _deleteExpandableRow(rowKey){
        let expandableRowToDelete = this._expandableRowComponents[rowKey];
        if (!_.isUndefined(this._expandableRowComponents[rowKey])) {
            this._expandableRowComponents[rowKey].on('RowUserExpanded', this._onExpandableRowUserExpanded);
            delete this._expandableRowComponents[rowKey];
            return expandableRowToDelete.shutdown();
        }
    }

    /**
     * Remove an expandable row from the DOM but keep the component intact
     * @param expandableRowComponent {ExpandableGridRowComponent}
     * @private
     */
    _removeExpandableRow(expandableRowComponent, keepState){
        if (keepState !== true) {
            expandableRowComponent.setExpanded(false);
        }
        expandableRowComponent.renderer.rootElement.remove();
        let rowChildren = expandableRowComponent.getChildren();
        rowChildren.forEach((child) => {
            if (!_.isUndefined(this._expandableRowComponents[child])) {
                this._removeExpandableRow(this._expandableRowComponents[child], keepState);
            } else if (!_.isUndefined(this._rowComponents[child])) {
                this._removeRow(this._rowComponents[child], keepState);
            }
        });
    }

    _removeRowChildren(expandableRow, keepState){
        let rowChildren = expandableRow.getChildren();
        rowChildren.forEach((child) => {
            if (!_.isUndefined(this._expandableRowComponents[child])) {
                this._removeExpandableRow(this._expandableRowComponents[child], keepState);
            } else if (!_.isUndefined(this._rowComponents[child])) {
                this._removeRow(this._rowComponents[child], keepState);
            }
        });
    }



    /**
     * Sort the grid based on current sort info
     * @private
     */
    _sortRows(){
        if (!_.isUndefined(this._sortColumn)) {
            // TODO: REvisit once server side sorting is in place
            // let sortedData = this._model.getData();
            // this._model.setData(sortedData);
            // this._model.setSelectedRows(this._model.getSelectedRows());
        }
    }

    /**
     * Select the passed rows
     * @param newVal {array}
     * @private
     */
    _setSelectedRows(newVal){
        for (let rowKey in this._rowComponents) {
            this._rowComponents[rowKey].setSelected(false);
        }
        newVal.forEach((rowId) => {
            this._rowComponents[rowId].setSelected(true);
        });
    }

    /**
     * Set or remove focus
     * @param newVal {boolean}
     * @private
     */
    _setFocus(newVal){
        if (newVal === true) {
            this._rootElem.focus();
        } else {
            this._rootElem.blur();
        }
    }


    _getRangeSelection(currentSelection, previousSelection){
        let inSelection = false,
            selection = [],
            entityOrder = this._model.getEntityOrder();
        entityOrder.forEach((entityKey) => {
            if (entityKey === currentSelection || entityKey === previousSelection) {
                if (inSelection === false) {
                    inSelection = true;
                } else {
                    selection.push(entityKey);
                    inSelection = false;
                }
            }
            if (inSelection === true) {
                let entityRowDomElement = this._gridBody.querySelector('[data-id="' + entityKey + '"]');
                if (entityRowDomElement !== null) {
                    selection.push(entityKey);
                }
            }
        });
        return selection;
    }



    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = EntityTreeGridRenderer;